import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom"; // Include useSearchParams
import Loading from "../../components/Loading";
import {
  Dropdown,
  DropdownMenu,
  DropdownOption,
  DropdownTrigger,
} from "../../components/ui/Dropdown";
import { useDispatch, useSelector } from "react-redux";
import {
  addList as addListApi,
  removeList as removeListApi,
  renameList as renameListApi,
} from "../../features/myListSlice/myListApi";
import { toast } from "react-toastify";
import { changeActiveListName, updateListNames } from "../../features/myListSlice/myListSlice";
import Button from "../../components/ui/Button";

const Lists = () => {
  const [isSelectingListName, setIsSelectingListName] = useState(false);
  const [newListName, setNewListName] = useState("");
  const [listNameToDelete, setListNameToDelete] = useState(false);
  const [typedListName, setTypedListName] = useState("");

  const { activeListName, listNames: rawListNames, status } = useSelector(
    (state) => state.myList
  );
  
  // Get stored list names on mount
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams(); // Initialize search params

  // Parse and normalize list names on component mount
  useEffect(() => {
    const storedListNames = localStorage.getItem("listNames");
    let parsedNames = [];
    
    try {
      if (storedListNames) {
        // Handle both JSON string and array string formats
        const cleaned = storedListNames.replace(/'/g, '"');
        parsedNames = JSON.parse(cleaned);
      }
    } catch (error) {
      console.error('Error parsing stored list names:', error);
      parsedNames = [];
    }

    // Ensure we have an array
    parsedNames = Array.isArray(parsedNames) ? parsedNames : [];
    
    if (parsedNames.length > 0) {
      dispatch(updateListNames(parsedNames));
      if (!activeListName) {
        dispatch(changeActiveListName(parsedNames[0]));
      }
    }
  }, []);

  // Parse listNames if it's a string
  const listNames = (() => {
    if (typeof rawListNames === 'string') {
      try {
        return JSON.parse(rawListNames.replace(/'/g, '"'));
      } catch (e) {
        console.error('Error parsing rawListNames:', e);
        return [];
      }
    }
    return Array.isArray(rawListNames) ? rawListNames : [];
  })();
    
  console.log('Lists Component - listNames after parsing:', listNames);

  const addList = async (e) => {
    e.preventDefault();

    if (status.addList !== "idle") return;

    if (!newListName.trim()) {
      toast.error("Please enter a list name");
      return;
    }

    if (listNames.includes(newListName.trim())) {
      toast.error("A list with this name already exists");
      return;
    }

    try {
      const result = await dispatch(addListApi({ navigate, newListName: newListName.trim() })).unwrap();
      
      // If we have valid list names, update the state
      if (result?.listNames) {
        dispatch(updateListNames(result.listNames));
        localStorage.setItem("listNames", JSON.stringify(result.listNames));
        dispatch(changeActiveListName(newListName.trim()));
        setIsSelectingListName(false);
        setNewListName("");
        toast.success("List created successfully!",{ autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error adding list:', error);
      toast.error(error.message || "Failed to add list");
    }
  };

  const removeList = async (e) => {
    e.preventDefault();

    if (listNameToDelete !== typedListName)
      return toast.error("List names do not match!");

    const { payload } = await dispatch(
      removeListApi({ navigate, listName: listNameToDelete })
    );

    if (payload.listName) {
      if (payload.listName === activeListName) {
        dispatch(changeActiveListName(listNames[0]));
      }
    }

    setListNameToDelete(null);
    setTypedListName("");
  };

  const newListInputRef = useRef();
  const deleteListInputRef = useRef();

  useEffect(() => {
    if (listNameToDelete) deleteListInputRef.current?.focus();
    else setTypedListName("");
  }, [listNameToDelete]);

  useEffect(() => {
    if (isSelectingListName) newListInputRef.current?.focus();
  }, [isSelectingListName]);

  const handleListChange = (listName) => {
    dispatch(changeActiveListName(listName));
    setSearchParams({ page: 1 }); // Reset URL to page 1
  };

  return (
    <>
      {listNameToDelete && (
        <div className="fixed z-[61] inset-0 isolate flex justify-center items-center">
          <div
            className="absolute inset-0 bg-black bg-opacity-30 cursor-pointer -z-10"
            onClick={() => setListNameToDelete(null)}
          ></div>

          <div className="w-[90%] max-w-[460px] bg-white flex flex-col gap-5 py-7 sm:py-10 px-4 sm:px-12 rounded-lg relative">
            <button
              className="absolute right-3 top-3"
              onClick={() => setListNameToDelete(null)}
            >
              <img src="/cross.svg" alt="cross" className="size-6" />
            </button>

            <div className="flex flex-col items-center text-center">
              <h6 className="text-[24px] sm:text-[28px] font-semibold">
                Confirm Delete List
              </h6>
              <p className="sm:text-18px">
                Enter{" "}
                <span className="font-semibold">'{listNameToDelete}'</span>{" "}
                below to confirm deleting your list
              </p>
            </div>

            <form onSubmit={removeList} className="flex">
              <input
                ref={deleteListInputRef}
                value={typedListName}
                onChange={(e) => setTypedListName(e.target.value)}
                placeholder="Enter list name"
                className="z-10 rounded-md h-12 w-full border px-2 text-[15px] focus-visible:outline-cherry focus-visible:outline-2 focus-visible:outline-double rounded-r-none border-r-0"
              />

              <Button
                state={status.removeList}
                disabled={status.removeList === "loading"}
                className="!h-12 rounded-l-none !px-3 !text-[15px]"
              >
                Confirm
              </Button>
            </form>
          </div>
        </div>
      )}

      <Dropdown triggers={[activeListName, status.addList]} className="order-4 border border-[#D30039] text-customRed rounded-md">
        <DropdownTrigger className="font-semibold gap-1 md:gap-3 border !h-11 px-2 md:px-3 transition-colors rounded-md text-sm lg:text-[15px] relative">
          <span>{activeListName === 'Voter List' ? 'My Voter List' : activeListName}</span>
          <img
            src={"/chevron-down-red.svg"}
            className="size-[1.4em]"
            width={24}
            height={24}
          />
        </DropdownTrigger>

        <DropdownMenu>
          {(Array.isArray(listNames) ? listNames : []).map((listName, i) => (
            <DropdownOption
              onClick={() => handleListChange(listName)}
              key={i}
              className="hover:bg-[#F9F9FA] transition-colors cursor-pointer h-9 flex items-center justify-between font-medium gap-2"
            >
              {listName === 'Voter List' ? 'My Voter List' : listName}
            </DropdownOption>
          ))}

          {/* {isSelectingListName ? (
            <DropdownOption
              isPreventClose={true}
              className={`relative transition-colors cursor-pointer h-9 flex items-center justify-between font-medium gap-2 hover:bg-white min-w-[147px]`}
            >
              <></>
              <form onSubmit={addList} className="absolute inset-0 flex">
                <input
                  ref={newListInputRef}
                  placeholder="List Name"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  className="rounded-l-md h-full w-full border border-r-0 px-2 text-sm outline-none"
                />

                {status.addList !== "idle" && (
                  <div className="border border-l-0 flex justify-center items-center w-10">
                    <Loading size={16} />
                  </div>
                )}

                {status.addList === "idle" && (
                  <button
                    className="border flex justify-center items-center w-10 transition-colors hover:bg-[#F9F9FA]"
                    type="button"
                    onClick={() => {
                      setIsSelectingListName(false);
                      setNewListName("");
                    }}
                  >
                    <img src="/cross.svg" alt="cross" className="w-4" />
                  </button>
                )}

                {status.addList === "idle" && (
                  <button
                    className="border flex justify-center items-center w-10 transition-colors hover:bg-[#F9F9FA]"
                    type="submit"
                  >
                    <img src="/check.svg" alt="check" className="w-5" />
                  </button>
                )}
              </form>
            </DropdownOption>
          ) : (
            <DropdownOption
              className={`relative transition-colors cursor-pointer h-9 flex items-center justify-between font-medium gap-2 hover:bg-[#F9F9FA] min-w-[147px]`}
              isPreventClose={true}
              onClick={() => setIsSelectingListName(true)}
            >
              <span>Add New List</span>
              <img
                src="/plus.svg"
                alt="add"
                className="w-[18px] sm:w-[22px] translate-y-[0.08em]"
              />
            </DropdownOption>
          )} */}
        </DropdownMenu>
      </Dropdown>
    </>
  );
};

export default Lists;